import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Link as LinkIcon, GraduationCap, FileText, Video, ExternalLink } from "lucide-react";
import { PaymentHistory } from "@/components/PaymentHistory";

function fmt(dt) {
  if (!dt) return "";
  try {
    return new Date(dt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return dt; }
}

function statusBadge(s) {
  const map = {
    upcoming: "bg-blue-100 text-blue-800",
    ongoing: "bg-emerald-100 text-emerald-800",
    completed: "bg-gray-100 text-gray-700",
    postponed: "bg-amber-100 text-amber-800",
    cancelled: "bg-rose-100 text-rose-700",
  };
  return map[s] || "bg-secondary text-foreground";
}

const KIND_ICONS = { zoom: Video, meet: Video, teams: Video, stream: Video, recording: Video, drive: FileText, material: FileText, other: LinkIcon };

export default function MyClasses() {
  const [classes, setClasses] = useState([]);
  const [resources, setResources] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/me/classes"),
      api.get("/me/resources"),
      api.get("/me/enrollments"),
    ]).then(([c, r, e]) => {
      setClasses(c.data);
      setResources(r.data);
      setEnrollments(e.data);
    }).finally(() => setLoading(false));
  }, []);

  const upcoming = classes.filter((c) => c.status !== "completed" && c.status !== "cancelled");
  const past = classes.filter((c) => c.status === "completed" || c.status === "cancelled");

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">My Learning</div>
        <h1 className="mt-1 font-display text-3xl font-bold">My Courses</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your purchased courses, classes, resources and payment history.</p>
      </div>

      {/* Enrolled courses */}
      <h2 className="font-display text-xl font-bold">Enrolled courses</h2>
      {loading ? (
        <div className="mt-3 text-sm text-muted-foreground">Loading...</div>
      ) : enrollments.length === 0 ? (
        <Card className="mt-3 border-dashed"><CardContent className="p-6 text-sm text-muted-foreground">
          You are not enrolled in any courses yet. <Link to="/courses" className="font-semibold text-[hsl(var(--brand-navy))] underline">Browse courses</Link>.
        </CardContent></Card>
      ) : (
        <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {enrollments.map((e) => (
            <Card key={e.id} className="border-border/70" data-testid={`my-course-${e.course?.slug}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <GraduationCap className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
                  <div>
                    <div className="font-display text-base font-bold">{e.course?.name || "Course"}</div>
                    <div className="text-xs text-muted-foreground">Enrolled {new Date(e.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  <span className={`rounded-full px-2 py-0.5 font-semibold uppercase ${e.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`} data-testid={`enrollment-status-${e.course?.slug}`}>{e.status}</span>
                  <span className="rounded-full bg-secondary px-2 py-0.5">{{ purchase: "Purchased", free: "Free", complimentary: "Complimentary", complimentary_legacy: "Complimentary" }[e.source] || "Enrolled"}</span>
                  <span className="rounded-full bg-secondary px-2 py-0.5">{e.expires_at ? `Until ${new Date(e.expires_at).toLocaleDateString()}` : "Lifetime"}</span>
                </div>
                <Button asChild size="sm" variant="outline" className="mt-3 w-full" data-testid={`view-course-${e.course?.slug}`}>
                  <Link to={`/courses/${e.course?.slug}`}>View course</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PaymentHistory />

      {/* Upcoming classes */}
      <h2 className="mt-10 font-display text-xl font-bold">Upcoming classes</h2>
      {upcoming.length === 0 ? (
        <Card className="mt-3 border-dashed"><CardContent className="p-6 text-sm text-muted-foreground">
          No upcoming classes scheduled. Admin will add your class times here.
        </CardContent></Card>
      ) : (
        <div className="mt-3 space-y-3">
          {upcoming.map((c) => (
            <Card key={c.id} className="border-border/70" data-testid={`class-${c.id}`}>
              <CardContent className="flex flex-wrap items-center gap-4 p-5">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <div className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusBadge(c.status)}`}>{c.status}</div>
                    <div className="text-xs font-mono uppercase text-muted-foreground">{c.class_type}</div>
                  </div>
                  <div className="mt-1 font-display text-base font-bold">{c.title}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmt(c.start_time)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> → {fmt(c.end_time)}</span>
                    {c.instructor_name && <span>👤 {c.instructor_name}</span>}
                  </div>
                  {c.description && <div className="mt-2 text-sm text-muted-foreground">{c.description}</div>}
                </div>
                {c.meeting_link ? (
                  <Button asChild className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid={`join-${c.id}`}>
                    <a href={c.meeting_link} target="_blank" rel="noreferrer">Join class <ExternalLink className="ml-1.5 h-4 w-4" /></a>
                  </Button>
                ) : (
                  <Button variant="outline" disabled>Link coming soon</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resources */}
      <h2 className="mt-10 font-display text-xl font-bold">My resources</h2>
      {resources.length === 0 ? (
        <Card className="mt-3 border-dashed"><CardContent className="p-6 text-sm text-muted-foreground">
          No study resources shared with you yet.
        </CardContent></Card>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {resources.map((r) => {
            const Icon = KIND_ICONS[r.kind] || LinkIcon;
            return (
              <Card key={r.id} className="border-border/70">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[hsl(var(--brand-navy))]/10 text-[hsl(var(--brand-navy))]"><Icon className="h-4 w-4" /></div>
                    <div className="flex-1">
                      <div className="font-display text-sm font-bold">{r.title}</div>
                      {r.description && <div className="mt-1 text-xs text-muted-foreground">{r.description}</div>}
                      <div className="mt-1 text-[10px] font-mono uppercase text-muted-foreground">{r.kind}</div>
                    </div>
                  </div>
                  <Button asChild size="sm" className="mt-3 w-full bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy))]/90" data-testid={`open-resource-${r.id}`}>
                    <a href={r.url} target="_blank" rel="noreferrer">Open <ExternalLink className="ml-1.5 h-4 w-4" /></a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {past.length > 0 && (
        <>
          <h2 className="mt-10 font-display text-xl font-bold">Past classes</h2>
          <div className="mt-3 space-y-2">
            {past.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm">
                <div>
                  <div className="font-medium">{c.title}</div>
                  <div className="text-xs text-muted-foreground">{fmt(c.start_time)}</div>
                </div>
                <div className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusBadge(c.status)}`}>{c.status}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
